import { Box, Center, Loader, Text, TextInput } from "@mantine/core";
import { useDebouncedValue, useMediaQuery } from "@mantine/hooks";
import { IconSearch, IconX } from "@tabler/icons-react";
import {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { booksApi } from "@/api/books";
import { MOBILE_MEDIA_QUERY } from "@/components/ui";
import {
  selectEffectiveReadingDirection,
  useReaderStore,
} from "@/store/readerStore";
import { ChapterTransitionPanel } from "./ChapterTransitionPanel";
import {
  useAdjacentBooks,
  useKeyboardNav,
  useReadProgress,
  useSeriesNavigation,
  useTouchNav,
} from "./hooks";
import { MobileReaderBottomBar } from "./MobileReaderBottomBar";
import { PdfContinuousScrollReader } from "./PdfContinuousScrollReader";
import { PdfReaderSettings } from "./PdfReaderSettings";
import { ReaderFirstRunHint } from "./ReaderFirstRunHint";
import { ReaderToolbar } from "./ReaderToolbar";

// Import CSS for text layer and annotation layer
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

export type PdfZoomLevel =
  | "fit-page"
  | "fit-width"
  | "50%"
  | "75%"
  | "100%"
  | "125%"
  | "150%"
  | "200%";

export interface PdfReaderProps {
  /** Book ID */
  bookId: string;
  /** Book title */
  title: string;
  /** Total pages (from backend metadata, may differ from actual PDF) */
  totalPages: number;
  /** Starting page from URL parameter */
  startPage?: number;
  /** Incognito mode - when true, progress tracking is disabled */
  incognito?: boolean;
  /** Callback when reader should close */
  onClose: () => void;
}

/**
 * Native PDF reader component using react-pdf (pdf.js).
 *
 * Features:
 * - Native PDF rendering with vector graphics
 * - Text selection and copy
 * - Search within document
 * - Multiple zoom levels
 * - Progress tracking
 */
export function PdfReader({
  bookId,
  title,
  totalPages: _backendTotalPages,
  startPage,
  incognito,
  onClose,
}: PdfReaderProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initializedBookIdRef = useRef<string | null>(null);

  // Local state for PDF-specific features
  const [numPages, setNumPages] = useState<number>(0);
  const [pageError, setPageError] = useState<string | null>(null);
  const [settingsOpened, setSettingsOpened] = useState(false);
  // Whether the user scrolled to the trailing "Next Chapter" panel in
  // continuous mode. Gates the auto-advance countdown so it only runs once the
  // panel is actually on screen.
  const [trailingReached, setTrailingReached] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText] = useDebouncedValue(searchText, 300);
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  });
  // Store actual PDF page dimensions from the first rendered page
  const [pdfPageDimensions, setPdfPageDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // On a phone-sized viewport, `fit-page` produces an unreadably small page
  // (portrait PDF scaled to a portrait viewport leaves text near ~33% width).
  // Default to `fit-width` on first render below `xs`; persisted per-book
  // choices would still win once we surface them, but zoom is currently local.
  // `getInitialValueInEffect: false` makes useMediaQuery match synchronously
  // on first render so the useState initializer below sees the real viewport.
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY, false, {
    getInitialValueInEffect: false,
  });
  const [zoomLevel, setZoomLevel] = useState<PdfZoomLevel>(() =>
    isMobile ? "fit-width" : "fit-page",
  );

  // Cycle through PDF zoom levels (for toolbar fit button)
  const cyclePdfZoom = useCallback(() => {
    setZoomLevel((current) => {
      switch (current) {
        case "fit-page":
          return "fit-width";
        case "fit-width":
          return "100%";
        case "100%":
          return "fit-page";
        default:
          return "fit-page";
      }
    });
  }, []);

  // Map PDF zoom level to FitMode for toolbar display
  const toolbarFitMode = useMemo(() => {
    switch (zoomLevel) {
      case "fit-page":
        return "screen" as const;
      case "fit-width":
        return "width" as const;
      default:
        return "original" as const;
    }
  }, [zoomLevel]);

  // Reader store state
  const currentPage = useReaderStore((state) => state.currentPage);
  const toolbarVisible = useReaderStore((state) => state.toolbarVisible);
  const isFullscreen = useReaderStore((state) => state.isFullscreen);
  const autoHideToolbar = useReaderStore(
    (state) => state.settings.autoHideToolbar,
  );
  const toolbarHideDelay = useReaderStore(
    (state) => state.settings.toolbarHideDelay,
  );
  const backgroundColor = useReaderStore(
    (state) => state.settings.backgroundColor,
  );
  const pdfSpreadMode = useReaderStore((state) => state.settings.pdfSpreadMode);
  const pdfContinuousScroll = useReaderStore(
    (state) => state.settings.pdfContinuousScroll,
  );
  const adjacentBooks = useReaderStore((state) => state.adjacentBooks);
  const boundaryView = useReaderStore((state) => state.boundaryView);
  const autoAdvanceToNextBook = useReaderStore(
    (state) => state.settings.autoAdvanceToNextBook,
  );
  const autoAdvanceSeconds = useReaderStore(
    (state) => state.settings.autoAdvanceSeconds,
  );
  const readingDirection = useReaderStore(selectEffectiveReadingDirection);

  // Reader store actions
  const initializeReader = useReaderStore((state) => state.initializeReader);
  const setBoundaryView = useReaderStore((state) => state.setBoundaryView);
  const correctTotalPages = useReaderStore((state) => state.correctTotalPages);
  const setToolbarVisible = useReaderStore((state) => state.setToolbarVisible);
  const setFullscreen = useReaderStore((state) => state.setFullscreen);
  const toggleToolbar = useReaderStore((state) => state.toggleToolbar);

  // Fetch adjacent books for series navigation
  useAdjacentBooks({ bookId, enabled: true });

  // Series navigation. The transition panels (in-flow for continuous, overlay
  // for paginated) replace the two-press boundary toast, so this no longer
  // consumes onBoundaryChange / handleEndBoundary.
  const {
    handleNextPage: baseNextPage,
    handlePrevPage: basePrevPage,
    goToNextBook,
    goToPrevBook,
    canGoNextBook,
    canGoPrevBook,
  } = useSeriesNavigation({
    onBeforeNavigateToNext: incognito
      ? undefined
      : () => {
          booksApi.markAsRead(bookId);
        },
  });

  // Spread-aware navigation: in double-page modes, move by 2 pages.
  // After the first step, check if we hit a boundary (boundaryState changed
  // from "none") — if so, don't call a second time or the overlay will be
  // skipped and the reader will immediately navigate to the next book.
  const handleNextPage = useCallback(() => {
    if (pdfSpreadMode === "single" || pdfContinuousScroll) {
      baseNextPage();
    } else {
      const boundaryBefore = useReaderStore.getState().boundaryState;
      baseNextPage();
      const boundaryAfter = useReaderStore.getState().boundaryState;
      if (boundaryBefore === "none" && boundaryAfter === "none") {
        baseNextPage();
      }
    }
  }, [pdfSpreadMode, pdfContinuousScroll, baseNextPage]);

  const handlePrevPage = useCallback(() => {
    if (pdfSpreadMode === "single" || pdfContinuousScroll) {
      basePrevPage();
    } else {
      const boundaryBefore = useReaderStore.getState().boundaryState;
      basePrevPage();
      const boundaryAfter = useReaderStore.getState().boundaryState;
      if (boundaryBefore === "none" && boundaryAfter === "none") {
        basePrevPage();
      }
    }
  }, [pdfSpreadMode, pdfContinuousScroll, basePrevPage]);

  // Read progress hook (use numPages from PDF if available, disabled in incognito mode)
  const effectiveTotalPages = numPages > 0 ? numPages : _backendTotalPages;
  const {
    initialPage,
    isLoading: progressLoading,
    saveProgress,
  } = useReadProgress({
    bookId,
    totalPages: effectiveTotalPages,
    enabled: !incognito,
  });

  // Paginated boundary navigation (single/double). Replaces the two-press
  // toast: paging past the last page raises the "Next Chapter" overlay (and
  // marks the book read); paging before page 1 raises "Previous Chapter". While
  // the overlay is up, "next"/"prev" either continue to the adjacent book or
  // dismiss the overlay. Continuous mode uses in-flow panels instead.
  const isDoublePaginated = pdfSpreadMode !== "single" && !pdfContinuousScroll;
  const handlePaginatedNext = useCallback(() => {
    const view = useReaderStore.getState().boundaryView;
    if (view !== "none") {
      if (view === "at-end") goToNextBook();
      else setBoundaryView("none");
      return;
    }
    // Continuous mode uses in-flow panels reached by scrolling, not the
    // overlay; keep keyboard paging there from raising a stale boundaryView.
    if (!pdfContinuousScroll) {
      const page = useReaderStore.getState().currentPage;
      const atEnd = isDoublePaginated
        ? page >= effectiveTotalPages - 1
        : page >= effectiveTotalPages;
      if (atEnd) {
        setBoundaryView("at-end");
        if (!incognito) saveProgress(effectiveTotalPages);
        return;
      }
    }
    handleNextPage();
  }, [
    pdfContinuousScroll,
    isDoublePaginated,
    effectiveTotalPages,
    incognito,
    saveProgress,
    goToNextBook,
    setBoundaryView,
    handleNextPage,
  ]);

  const handlePaginatedPrev = useCallback(() => {
    const view = useReaderStore.getState().boundaryView;
    if (view !== "none") {
      if (view === "at-start") goToPrevBook();
      else setBoundaryView("none");
      return;
    }
    if (!pdfContinuousScroll) {
      const page = useReaderStore.getState().currentPage;
      if (page <= 1) {
        setBoundaryView("at-start");
        return;
      }
    }
    handlePrevPage();
  }, [pdfContinuousScroll, goToPrevBook, setBoundaryView, handlePrevPage]);

  // Reset the trailing-panel reached flag when the book changes (render-phase
  // pattern, per the React docs) in case the reader is reused across a
  // navigation rather than remounted.
  const [prevBookId, setPrevBookId] = useState(bookId);
  if (bookId !== prevBookId) {
    setPrevBookId(bookId);
    setTrailingReached(false);
  }

  // Calculate page dimensions based on zoom level
  // Note: PDF scale 1.0 = 72 DPI, which is small on modern displays
  // We use a base scale of 1.5 to make 100% appear at a readable size
  // (roughly equivalent to viewing at 96 DPI on a standard display)
  const BASE_SCALE = 1.5;

  const getPageDimensions = useCallback((): {
    width?: number;
    height?: number;
    scale?: number;
    // Indicates if scale needs halving for double-page mode (only percentage zoom levels)
    needsSpreadAdjustment?: boolean;
  } => {
    const isDoublePageMode = pdfSpreadMode !== "single";

    // For scale-based zoom levels, we don't need container dimensions
    // This ensures zoom changes work even before container is measured
    // These return the "full" scale - will be halved by spreadPageDimensions for double-page
    switch (zoomLevel) {
      case "50%":
        return { scale: 0.5 * BASE_SCALE, needsSpreadAdjustment: true };
      case "75%":
        return { scale: 0.75 * BASE_SCALE, needsSpreadAdjustment: true };
      case "100%":
        return { scale: 1.0 * BASE_SCALE, needsSpreadAdjustment: true };
      case "125%":
        return { scale: 1.25 * BASE_SCALE, needsSpreadAdjustment: true };
      case "150%":
        return { scale: 1.5 * BASE_SCALE, needsSpreadAdjustment: true };
      case "200%":
        return { scale: 2.0 * BASE_SCALE, needsSpreadAdjustment: true };
    }

    // For fit modes, we need container dimensions
    if (!containerDimensions.width || !containerDimensions.height) {
      // Fallback to scale-based rendering until container is measured
      // Use a reasonable scale that fills more of the viewport
      // These are already tuned for single vs double, no further adjustment needed
      return { scale: isDoublePageMode ? BASE_SCALE * 0.8 : BASE_SCALE * 1.2 };
    }

    // Always reserve space for toolbar since it can appear at any time
    const toolbarHeight = 64;
    // Padding around the page(s) - the page container has padding: 20px
    const padding = 40; // 20px on each side
    const gap = isDoublePageMode ? 8 : 0;
    const availableWidth = containerDimensions.width - padding;
    // Subtract toolbar height and padding from both top and bottom
    const availableHeight =
      containerDimensions.height - toolbarHeight - padding;

    // For double-page mode, each page gets half the available width
    const perPageWidth = isDoublePageMode
      ? Math.floor((availableWidth - gap) / 2)
      : availableWidth;

    // PDF pages at scale=1 are 72 DPI, so dimensions are in points (1/72 inch)
    // Default to US Letter size (612x792 points) if not yet measured
    const pageWidth = pdfPageDimensions?.width ?? 612;
    const pageHeight = pdfPageDimensions?.height ?? 792;

    switch (zoomLevel) {
      case "fit-page": {
        // Fit page: scale to fit within both width and height constraints
        // Calculate which dimension is the limiting factor
        const scaleByWidth = perPageWidth / pageWidth;
        const scaleByHeight = availableHeight / pageHeight;
        // Use the smaller scale to ensure it fits both constraints
        const fitScale = Math.min(scaleByWidth, scaleByHeight);
        return { scale: fitScale };
      }
      case "fit-width": {
        // Fit width: scale to fill available width, allow vertical scrolling
        const scaleByWidth = perPageWidth / pageWidth;
        return { scale: scaleByWidth };
      }
      default:
        return { scale: BASE_SCALE };
    }
  }, [containerDimensions, zoomLevel, pdfSpreadMode, pdfPageDimensions]);

  // Initialize reader when PDF loads and progress is ready
  useEffect(() => {
    if (
      !progressLoading &&
      numPages > 0 &&
      initializedBookIdRef.current !== bookId
    ) {
      initializedBookIdRef.current = bookId;

      let effectiveStartPage: number;
      if (startPage && startPage >= 1 && startPage <= numPages) {
        effectiveStartPage = startPage;
      } else {
        effectiveStartPage = initialPage;
      }

      initializeReader(bookId, numPages, effectiveStartPage);
    }
  }, [
    bookId,
    numPages,
    startPage,
    initialPage,
    progressLoading,
    initializeReader,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      initializedBookIdRef.current = null;
      useReaderStore.getState().resetSession();
    };
  }, []);

  // Track ResizeObserver instance for cleanup
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // Ref callback to set up ResizeObserver when container is attached
  // This solves the timing issue where useLayoutEffect runs before ref is attached
  // (e.g., when there's an early return for loading state on page reload)
  const setContainerRef = useCallback((element: HTMLDivElement | null) => {
    // Clean up previous observer
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }

    // Update the ref
    (containerRef as React.MutableRefObject<HTMLDivElement | null>).current =
      element;

    // Set up new observer if element exists
    if (element) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          setContainerDimensions({ width, height });
        }
      });
      resizeObserver.observe(element);
      resizeObserverRef.current = resizeObserver;
    }
  }, []);

  // Cleanup ResizeObserver on unmount
  useEffect(() => {
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);

  // Fullscreen handling
  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [setFullscreen]);

  // Enter/exit fullscreen
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (isFullscreen && !document.fullscreenElement) {
      container.requestFullscreen?.().catch(() => {
        setFullscreen(false);
      });
    } else if (!isFullscreen && document.fullscreenElement) {
      document.exitFullscreen?.();
    }
  }, [isFullscreen, setFullscreen]);

  // Auto-hide toolbar. Suppressed while a paginated transition overlay is up so
  // the toolbar (close/settings) and bottom nav bar stay reachable.
  const resetHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }

    if (autoHideToolbar && toolbarVisible && boundaryView === "none") {
      hideTimeoutRef.current = setTimeout(() => {
        setToolbarVisible(false);
      }, toolbarHideDelay);
    }
  }, [
    autoHideToolbar,
    toolbarVisible,
    toolbarHideDelay,
    setToolbarVisible,
    boundaryView,
  ]);

  useEffect(() => {
    resetHideTimeout();
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [resetHideTimeout]);

  // Reveal the toolbar whenever the paginated transition overlay appears.
  useEffect(() => {
    if (boundaryView !== "none") {
      setToolbarVisible(true);
    }
  }, [boundaryView, setToolbarVisible]);

  // Chapter-transition panels. Continuous mode mounts them in-flow (leading
  // before page 1, trailing after the last page); paginated mode renders one as
  // an overlay driven by boundaryView. Same component, both modes.
  const panelReadingDirection = readingDirection === "rtl" ? "rtl" : "ltr";
  const continuousLeadingPanel = (
    <ChapterTransitionPanel
      direction="prev"
      book={adjacentBooks?.prev ?? null}
      onContinue={goToPrevBook}
      readingDirection={panelReadingDirection}
    />
  );
  const continuousTrailingPanel = (
    <ChapterTransitionPanel
      direction="next"
      book={adjacentBooks?.next ?? null}
      onContinue={goToNextBook}
      autoAdvance={autoAdvanceToNextBook && trailingReached}
      countdownSeconds={autoAdvanceSeconds}
      readingDirection={panelReadingDirection}
    />
  );

  // Show toolbar on mouse / pen move. Skip touch — synthetic mouse events
  // fire after every tap on touch devices, which would pop the toolbar open
  // every time the user paged forward via a side-zone tap.
  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (e.pointerType === "touch") return;
      if (!toolbarVisible) {
        setToolbarVisible(true);
      }
      resetHideTimeout();
    },
    [toolbarVisible, setToolbarVisible, resetHideTimeout],
  );

  // Keyboard navigation
  useKeyboardNav({
    enabled: !settingsOpened && !searchOpen,
    onEscape: searchOpen ? () => setSearchOpen(false) : onClose,
    onNextPage: handlePaginatedNext,
    onPrevPage: handlePaginatedPrev,
  });

  // Touch/tap navigation for mobile devices.
  // In continuous-scroll mode pages are navigated by scrolling, so the whole
  // surface toggles the toolbar (tapZones: false) — the only way to reveal it
  // on a phone there, since pointer-move is mouse-only. Paginated mode keeps
  // tap zones (outer thirds page, center toggles).
  const { touchRef } = useTouchNav({
    enabled: !settingsOpened && !searchOpen,
    tapZones: !pdfContinuousScroll,
    onNextPage: handlePaginatedNext,
    onPrevPage: handlePaginatedPrev,
    onTap: toggleToolbar,
  });

  // Combined ref callback for page container (both pageContainerRef and touchRef)
  const setPageContainerRef = useCallback(
    (element: HTMLDivElement | null) => {
      // Update the regular ref
      (
        pageContainerRef as React.MutableRefObject<HTMLDivElement | null>
      ).current = element;
      // Update touch ref
      touchRef(element);
    },
    [touchRef],
  );

  // Handle Ctrl+F for search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "f") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // PDF document load success
  // When the real PDF page count is less than the backend metadata, correct the
  // store so boundary detection (overlay) fires at the real last page.
  const handleDocumentLoadSuccess = useCallback(
    ({ numPages: pdfNumPages }: { numPages: number }) => {
      setNumPages(pdfNumPages);
      setPageError(null);
      if (pdfNumPages < _backendTotalPages) {
        correctTotalPages(pdfNumPages);
      }
    },
    [_backendTotalPages, correctTotalPages],
  );

  // PDF document load error
  const handleDocumentLoadError = useCallback((error: Error) => {
    console.error("Error loading PDF:", error);
    setPageError(error.message || "Failed to load PDF");
  }, []);

  // Capture PDF page dimensions on first render for accurate fit calculations
  const handlePageRenderSuccess = useCallback(
    (page: { originalWidth: number; originalHeight: number }) => {
      // Only set once - all pages in a PDF typically have the same dimensions
      if (!pdfPageDimensions) {
        setPdfPageDimensions({
          width: page.originalWidth,
          height: page.originalHeight,
        });
      }
    },
    [pdfPageDimensions],
  );

  // Sync URL query parameter with current page
  useEffect(() => {
    if (currentPage > 0 && initializedBookIdRef.current !== null) {
      const url = new URL(window.location.href);
      url.searchParams.set("page", String(currentPage));
      window.history.replaceState(null, "", url.toString());
    }
  }, [currentPage]);

  // Get PDF file URL
  const pdfUrl = useMemo(() => `/api/v1/books/${bookId}/file`, [bookId]);

  // Page dimensions for rendering
  const pageDimensions = useMemo(
    () => getPageDimensions(),
    [getPageDimensions],
  );

  // Calculate spread page dimensions
  // For double-page mode, percentage zoom levels need to be halved so two pages fit
  // Fit modes already account for double-page in their calculation (using perPageWidth)
  const spreadPageDimensions = useMemo(() => {
    if (pdfSpreadMode === "single") {
      return pageDimensions;
    }
    // Only halve scale for percentage zoom levels (marked with needsSpreadAdjustment)
    if (
      pageDimensions.needsSpreadAdjustment &&
      pageDimensions.scale !== undefined
    ) {
      return { scale: pageDimensions.scale * 0.5 };
    }
    // Fit modes already calculated correct per-page scale, pass through
    return pageDimensions;
  }, [pageDimensions, pdfSpreadMode]);

  // Calculate which pages to display based on spread mode
  const spreadPages = useMemo((): {
    left: number | null;
    right: number | null;
  } => {
    if (pdfSpreadMode === "single") {
      return { left: currentPage, right: null };
    }

    if (pdfSpreadMode === "double") {
      // Double mode: show pages in pairs (1-2, 3-4, etc.)
      // Current page determines the spread
      const isOddPage = currentPage % 2 === 1;
      if (isOddPage) {
        // Odd page is on left
        return {
          left: currentPage,
          right: currentPage + 1 <= numPages ? currentPage + 1 : null,
        };
      } else {
        // Even page - show with previous odd page
        return {
          left: currentPage - 1,
          right: currentPage,
        };
      }
    }

    // double-odd: First page alone, then pairs starting from even pages (2-3, 4-5, etc.)
    // This is typical for books where page 1 is the cover
    if (currentPage === 1) {
      return { left: 1, right: null };
    }
    const isEvenPage = currentPage % 2 === 0;
    if (isEvenPage) {
      // Even page is on left
      return {
        left: currentPage,
        right: currentPage + 1 <= numPages ? currentPage + 1 : null,
      };
    } else {
      // Odd page (except 1) - show with previous even page
      return {
        left: currentPage - 1,
        right: currentPage,
      };
    }
  }, [currentPage, numPages, pdfSpreadMode]);

  // Determine if showing a single page in double-page mode (e.g., page 1 in double-odd)
  // When showing just one page, it should use full width, not half
  const isSinglePageInSpread = useMemo(() => {
    if (pdfSpreadMode === "single") return false;
    return spreadPages.right === null;
  }, [pdfSpreadMode, spreadPages]);

  // Check if fit modes require container dimensions that aren't ready yet
  const isFitMode = zoomLevel === "fit-page" || zoomLevel === "fit-width";
  const containerReady =
    containerDimensions.width > 0 && containerDimensions.height > 0;

  // Background color style
  const bgColor = useMemo(() => {
    switch (backgroundColor) {
      case "black":
        return "#000";
      case "gray":
        return "#404040";
      case "white":
        return "#fff";
      default:
        return "#000";
    }
  }, [backgroundColor]);

  // Container style
  const containerStyle: CSSProperties = useMemo(
    () => ({
      width: "100vw",
      height: "100dvh",
      position: "relative",
      overflow: "hidden",
      backgroundColor: bgColor,
    }),
    [bgColor],
  );

  // Page container style.
  // The container shrinks to sit *below* the toolbar (and above the bottom
  // bar) when they're visible, so a fit-width page taller than the viewport
  // can scroll its top edge into view instead of staying hidden under the
  // 64px toolbar. `margin: auto` on the page wrapper still centers short
  // pages within whatever vertical space is available, and pins to the top
  // (scrolling normally) when content overflows.
  const pageContainerStyle: CSSProperties = useMemo(
    () => ({
      position: "absolute",
      top: toolbarVisible
        ? "calc(env(safe-area-inset-top, 0px) + 64px)"
        : "env(safe-area-inset-top, 0px)",
      left: 0,
      right: 0,
      bottom: "env(safe-area-inset-bottom, 0px)",
      overflow: "auto",
      display: "flex",
      justifyContent: "center",
      alignItems: "flex-start",
      padding: "20px",
      transition: "top 0.2s ease-in-out",
    }),
    [toolbarVisible],
  );

  // Loading state
  if (progressLoading && numPages === 0) {
    return (
      <Center
        style={{ width: "100vw", height: "100dvh", backgroundColor: "#000" }}
      >
        <Loader size="lg" color="gray" />
      </Center>
    );
  }

  return (
    <Box
      ref={setContainerRef}
      onPointerMove={handlePointerMove}
      style={containerStyle}
    >
      {/* Toolbar */}
      <ReaderToolbar
        title={title}
        visible={toolbarVisible}
        onClose={onClose}
        onOpenSettings={() => setSettingsOpened(true)}
        prevBook={adjacentBooks?.prev}
        nextBook={adjacentBooks?.next}
        onPrevBook={canGoPrevBook ? goToPrevBook : undefined}
        onNextBook={canGoNextBook ? goToNextBook : undefined}
        fitMode={toolbarFitMode}
        onCycleFitMode={cyclePdfZoom}
      />

      {/* Phone-only bottom navigation. Hidden in continuous scroll mode
          where the page-counter / slider don't apply (user scrolls). */}
      {!pdfContinuousScroll && (
        <MobileReaderBottomBar
          visible={toolbarVisible}
          onPrevPage={handlePaginatedPrev}
          onNextPage={handlePaginatedNext}
        />
      )}

      {/* First-run hint: teaches phone users that center-tap reveals the
          toolbar. Once per session across all reader formats. */}
      <ReaderFirstRunHint />

      {/* Search bar (when open) */}
      {searchOpen && (
        <Box
          style={{
            position: "absolute",
            top: toolbarVisible ? 64 : 0,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 100,
            backgroundColor: "rgba(0,0,0,0.9)",
            padding: "8px 16px",
            borderRadius: "0 0 8px 8px",
          }}
        >
          <TextInput
            placeholder="Search in PDF..."
            size="sm"
            leftSection={<IconSearch size={16} />}
            rightSection={
              searchText ? (
                <IconX
                  size={16}
                  style={{ cursor: "pointer" }}
                  onClick={() => setSearchText("")}
                />
              ) : null
            }
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: "min(300px, calc(100vw - 32px))" }}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setSearchOpen(false);
              }
            }}
          />
        </Box>
      )}

      {/* PDF Document - Continuous Scroll or Paginated */}
      {pdfContinuousScroll ? (
        <Box
          style={{
            position: "absolute",
            top: toolbarVisible ? 64 : "env(safe-area-inset-top, 0px)",
            left: 0,
            right: 0,
            bottom: toolbarVisible ? 0 : "env(safe-area-inset-bottom, 0px)",
            transition: "top 0.2s ease-in-out, bottom 0.2s ease-in-out",
          }}
        >
          <PdfContinuousScrollReader
            bookId={bookId}
            totalPages={numPages}
            initialPage={currentPage}
            zoomLevel={zoomLevel}
            backgroundColor={backgroundColor}
            searchText={debouncedSearchText}
            onDocumentLoadSuccess={handleDocumentLoadSuccess}
            onDocumentLoadError={handleDocumentLoadError}
            tapRef={touchRef}
            leadingSlot={continuousLeadingPanel}
            trailingSlot={continuousTrailingPanel}
            onTrailingReachedChange={setTrailingReached}
          />
        </Box>
      ) : (
        <Box
          ref={setPageContainerRef}
          style={{
            ...pageContainerStyle,
            // Click-only navigation: we no longer compete with the browser's
            // native gestures, so allow standard pan + pinch-zoom for users
            // who want to scroll long pages or zoom into small text.
            touchAction: "manipulation",
          }}
        >
          {pageError ? (
            <Center style={{ width: "100%", height: "100%" }}>
              <Text c="red">{pageError}</Text>
            </Center>
          ) : (
            // `<Document>` is the direct flex child of the scrolling parent
            // and renders a content-sized block div with no style prop. Apply
            // `margin: auto` here so it consumes the cross-axis free space
            // (centering short pages) while still pinning to the top when
            // the page is taller than the viewport.
            <Box style={{ margin: "auto" }}>
              <Document
                file={pdfUrl}
                onLoadSuccess={handleDocumentLoadSuccess}
                onLoadError={handleDocumentLoadError}
                loading={
                  <Center
                    style={{
                      width: "100%",
                      height: "calc(100dvh - 128px)",
                      backgroundColor: "transparent",
                    }}
                  >
                    <Loader size="lg" color="gray" />
                  </Center>
                }
              >
                {/* Wait for container dimensions before rendering pages in fit modes */}
                {isFitMode && !containerReady ? (
                  <Center
                    style={{
                      width: "100%",
                      height: "calc(100dvh - 128px)",
                      backgroundColor: "transparent",
                    }}
                  >
                    <Loader size="lg" color="gray" />
                  </Center>
                ) : (
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: pdfSpreadMode !== "single" ? "8px" : "0",
                      justifyContent: "center",
                      alignItems: "flex-start",
                    }}
                  >
                    {/* Left page (or single page) */}
                    {spreadPages.left && (
                      <Page
                        pageNumber={spreadPages.left}
                        {...(pdfSpreadMode === "single" || isSinglePageInSpread
                          ? pageDimensions
                          : spreadPageDimensions)}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                        onRenderSuccess={handlePageRenderSuccess}
                        loading={
                          <Center
                            style={{
                              width: 300,
                              height: 400,
                              backgroundColor: "transparent",
                            }}
                          >
                            <Loader size="md" color="gray" />
                          </Center>
                        }
                        customTextRenderer={
                          debouncedSearchText
                            ? ({ str }) => {
                                if (!debouncedSearchText) return str;
                                const regex = new RegExp(
                                  `(${debouncedSearchText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
                                  "gi",
                                );
                                const parts = str.split(regex);
                                return parts
                                  .map((part) =>
                                    regex.test(part)
                                      ? `<mark style="background-color: yellow; padding: 0;">${part}</mark>`
                                      : part,
                                  )
                                  .join("");
                              }
                            : undefined
                        }
                      />
                    )}
                    {/* Right page (only in spread modes) */}
                    {spreadPages.right && (
                      <Page
                        pageNumber={spreadPages.right}
                        {...spreadPageDimensions}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                        loading={
                          <Center
                            style={{
                              width: 300,
                              height: 400,
                              backgroundColor: "transparent",
                            }}
                          >
                            <Loader size="md" color="gray" />
                          </Center>
                        }
                        customTextRenderer={
                          debouncedSearchText
                            ? ({ str }) => {
                                if (!debouncedSearchText) return str;
                                const regex = new RegExp(
                                  `(${debouncedSearchText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
                                  "gi",
                                );
                                const parts = str.split(regex);
                                return parts
                                  .map((part) =>
                                    regex.test(part)
                                      ? `<mark style="background-color: yellow; padding: 0;">${part}</mark>`
                                      : part,
                                  )
                                  .join("");
                              }
                            : undefined
                        }
                      />
                    )}
                  </Box>
                )}
              </Document>
            </Box>
          )}
        </Box>
      )}

      {/* Chapter-transition overlay for paginated PDF modes. Continuous mode
          uses in-flow panels instead, so this only applies to single/double. */}
      {!pdfContinuousScroll && boundaryView !== "none" && (
        <Box style={{ position: "absolute", inset: 0, zIndex: 50 }}>
          <ChapterTransitionPanel
            direction={boundaryView === "at-end" ? "next" : "prev"}
            book={
              boundaryView === "at-end"
                ? (adjacentBooks?.next ?? null)
                : (adjacentBooks?.prev ?? null)
            }
            onContinue={boundaryView === "at-end" ? goToNextBook : goToPrevBook}
            autoAdvance={boundaryView === "at-end" && autoAdvanceToNextBook}
            countdownSeconds={autoAdvanceSeconds}
            readingDirection={panelReadingDirection}
          />
        </Box>
      )}

      {/* Settings modal */}
      <PdfReaderSettings
        opened={settingsOpened}
        onClose={() => setSettingsOpened(false)}
        zoomLevel={zoomLevel}
        onZoomChange={setZoomLevel}
      />
    </Box>
  );
}
